import React, { useState, useMemo } from 'react';
import { 
  LayoutDashboard, Compass, Shield, BrainCircuit, Target, BarChart3, Bot, 
  Zap, FileText, Github, TrendingUp, Sparkles, Scale, Map, Users, AlertTriangle, 
  BookOpen, ThumbsUp, ThumbsDown, Bell, Package, ArrowUp, ArrowDown, FlaskConical,
  ListTodo, Eye, CheckSquare, Clock, Download, Printer, Loader2, ServerCrash
} from 'lucide-react';

// --- Icon Mapping ---
const iconMap = {
  default: <Sparkles className="h-5 w-5" />,
  funding: <BarChart3 className="h-5 w-5 text-green-400" />,
  competitor: <Shield className="h-5 w-5 text-red-400" />,
  tech: <Zap className="h-5 w-5 text-blue-400" />,
  client: <Target className="h-5 w-5 text-yellow-400" />,
  pipeline: <ListTodo className="h-5 w-5 text-purple-400" />,
  report: <FileText className="h-5 w-5 text-indigo-400" />,
  market: <TrendingUp className="h-5 w-5 text-green-400" />,
};

// --- Main App Component ---
export default function App() {
  const [activeView, setActiveView] = useState('dashboard');

  // State for the different AI layers. Initialized as empty.
  const [marketData, setMarketData] = useState({
    forecast: "Click 'Generate New Vision Report' to fetch live data from the Monado API...",
    sectors: [],
    opportunities: [],
  });
  const [competitorData, setCompetitorData] = useState({
    activity: [],
    swot: { strengths: [], weaknesses: [], opportunities: [], threats: [] },
  });
  const [innovationData, setInnovationData] = useState({
    map: { hot: [], fading: [] },
    bank: [],
    momentum: [],
  });
  const [clientData, setClientData] = useState({
    clients: [],
    heatmap: [],
  });
  
  // State for the Strategic Reports
  const [strategicReport, setStrategicReport] = useState(null);
  
  // State for the Opportunity Pipeline (Managed in-memory)
  const [pipelineData, setPipelineData] = useState({
    columns: [
      { id: 'new', title: 'New Signals' },
      { id: 'review', title: 'Under Review' },
      { id: 'poc', title: 'POC in Progress' },
      { id: 'actioned', title: 'Actioned' },
    ],
    cards: [
      { id: 'card-1', title: "Analyze 'SaaSify' Chatbot", content: "L2 Alert: New competitor product.", type: 'competitor', column: 'new' },
      { id: 'card-2', title: "Pitch 'Apex Logistics'", content: "L4 Client: Readiness Score +12%", type: 'client', column: 'new' },
      { id: 'card-3', title: "POC: 'Pico-2' LLM", content: "L3 R&D: New lightweight model detected.", type: 'tech', column: 'review' },
      { id: 'card-4', title: "Explore FinTech AI Reg-Tech", content: "L1 Forecast: High-growth sector.", type: 'market', column: 'new' },
    ],
  });

  // Loading and Error states for API calls
  const [isLoading, setIsLoading] = useState({
    l1: false, l2: false, l3: false, l4: false, report: false
  });
  const [error, setError] = useState({
    l1: null, l2: null, l3: null, l4: null, report: null
  });

  // Base URL for the Monado API Backend
  const API_BASE_URL = "http://127.0.0.1:8000";

  // --- API Call Handler ---
  const handleGenerateReport = async (layer) => {
    setIsLoading(prev => ({ ...prev, [layer]: true }));
    setError(prev => ({ ...prev, [layer]: null }));
    
    let url = "";
    let setData;
    let errorMsg = "";

    try {
      if (layer === 'l1') {
        url = `${API_BASE_URL}/api/l1/market-vision`;
        setData = setMarketData;
      } else if (layer === 'l2') {
        url = `${API_BASE_URL}/api/l2/competitive-foresight`;
        setData = setCompetitorData;
      } else if (layer === 'l3') {
        url = `${API_BASE_URL}/api/l3/innovation-pulse`;
        setData = setInnovationData;
      } else if (layer === 'l4') {
        url = `${API_BASE_URL}/api/l4/client-intelligence`;
        setData = setClientData;
      }

      if (url && setData) {
        const response = await fetch(url);
        if (!response.ok) {
          const errData = await response.json();
          throw new Error(errData.detail || `API request failed: ${response.statusText}`);
        }
        const data = await response.json();
        setData(data);
      }
      
    } catch (err) {
      console.error(`Failed to fetch ${layer} data:`, err);
      errorMsg = `**Error:** Failed to connect to Monado API at ${url}.\n\nIs the Python server running? (${err.message})`;
      setError(prev => ({ ...prev, [layer]: errorMsg }));
      
      // Set empty/error state for the specific layer
      if (layer === 'l1') {
        setMarketData({ forecast: errorMsg, sectors: [], opportunities: [] });
      } else if (layer === 'l2') {
        setCompetitorData({ activity: [], swot: { strengths: ['Error'], weaknesses: [err.message], opportunities: [], threats: [] } });
      } else if (layer === 'l3') {
        setInnovationData({ map: { hot: ['Error'], fading: [err.message] }, bank: [], momentum: [] });
      } else if (layer === 'l4') {
        setClientData({ clients: [], heatmap: [] });
      }
    } finally {
      setIsLoading(prev => ({ ...prev, [layer]: false }));
    }
  };
  
  // --- Pipeline Drag-and-Drop Handler ---
  const handleCardDrop = (cardId, newColumnId) => {
    setPipelineData(prevData => {
      const cardIndex = prevData.cards.findIndex(c => c.id === cardId);
      if (cardIndex === -1) return prevData;

      const newCards = [...prevData.cards];
      newCards[cardIndex].column = newColumnId;
      
      return { ...prevData, cards: newCards };
    });
  };

  // --- Memoize Dashboard Data ---
  // Moved from renderView to the top level of the component to fix Hook error
  const dashboardInsights = useMemo(() => [
    ...clientData.clients.slice(0, 1).map(c => ({ id: `c-${c.id}`, type: 'client', title: `High-Value Client: ${c.name}`, text: `Sector: ${c.sector}, Readiness: ${c.readinessScore}`, time: 'Live' })),
    ...competitorData.activity.slice(0, 2).map(a => ({ id: `a-${a.id}`, type: 'competitor', title: `Competitor Move: ${a.name}`, text: a.activity, time: a.time })),
    ...innovationData.map.hot.slice(0, 1).map((h, i) => ({ id: `h-${i}`, type: 'tech', title: `Trending Tech: ${h}`, text: 'L3 Alert: High developer momentum.', time: 'Live' })),
    ...marketData.opportunities.slice(0, 1).map(o => ({ id: `o-${o.name}`, type: 'market', title: `New Opportunity: ${o.name}`, text: `Market Score: ${o.score}`, time: 'Live' })),
  ], [clientData, competitorData, innovationData, marketData]);

  const dashboardAlerts = useMemo(() => [
    { id: 1, type: 'competitor', text: competitorData.activity[0]?.activity || 'SaaSify new product launch.' },
    { id: 2, type: 'client', text: `High-readiness score for ${clientData.clients[0]?.name || 'Apex Logistics'}` },
    { id: 3, type: 'tech', text: `New LLM '${innovationData.map.hot[0] || 'Pico-2'}' is trending.` },
  ], [competitorData, clientData, innovationData]);


  // --- Render Active View ---
  const renderView = () => {
    switch (activeView) {
      case 'dashboard':
        return <StrategicNexusDashboard 
          insights={dashboardInsights}
          alerts={dashboardAlerts}
          pipelineSummary={pipelineData}
        />;
      case 'l1':
        return <MarketVisionAI 
          data={marketData} 
          onGenerate={() => handleGenerateReport('l1')} 
          isLoading={isLoading.l1} 
          error={error.l1}
        />;
      case 'l2':
        return <CompetitiveForesightHub 
          data={competitorData}
          onGenerate={() => handleGenerateReport('l2')}
          isLoading={isLoading.l2}
          error={error.l2}
        />;
      case 'l3':
        return <InnovationPulseTracker 
          data={innovationData}
          onGenerate={() => handleGenerateReport('l3')}
          isLoading={isLoading.l3}
          error={error.l3}
        />;
      case 'l4':
        return <ClientIntelligenceEngine 
          data={clientData}
          onGenerate={() => handleGenerateReport('l4')}
          isLoading={isLoading.l4}
          error={error.l4}
        />;
      case 'pipeline':
        return <OpportunityPipeline 
          data={pipelineData} 
          onCardDrop={handleCardDrop} 
        />;
      case 'reports':
        return <StrategicReports 
          layers={{ l1: marketData, l2: competitorData, l3: innovationData, l4: clientData }}
          report={strategicReport}
          setReport={setStrategicReport}
          isLoading={isLoading.report}
        />;
      default:
        return <StrategicNexusDashboard insights={[]} alerts={[]} pipelineSummary={pipelineData} />;
    }
  };

  return (
    <div className="flex h-screen w-full bg-gray-900 text-gray-100 font-inter">
      {/* --- Sidebar --- */}
      <Sidebar activeView={activeView} setActiveView={setActiveView} />

      {/* --- Main Content Area --- */}
      <main className="flex-1 overflow-auto p-6 md:p-10">
        {renderView()}
      </main>
    </div>
  );
}

// --- Sidebar Component ---
function Sidebar({ activeView, setActiveView }) {
  const navItems = [
    { id: 'dashboard', icon: LayoutDashboard, text: 'Nexus Dashboard', section: 'Home' },
    { id: 'l1', icon: Compass, text: 'Market Vision AI', section: 'Intelligence Layers' },
    { id: 'l2', icon: Shield, text: 'Competitive Foresight', section: 'Intelligence Layers' },
    { id: 'l3', icon: BrainCircuit, text: 'Innovation Pulse', section: 'Intelligence Layers' },
    { id: 'l4', icon: Target, text: 'Client Intelligence', section: 'Intelligence Layers' },
    { id: 'pipeline', icon: ListTodo, text: 'Opportunity Pipeline', section: 'Action' },
    { id: 'reports', icon: FileText, text: 'Strategic Reports', section: 'Action' },
  ];

  return (
    <nav className="flex w-64 flex-col border-r border-gray-800 bg-gray-950 p-4">
      <div className="flex items-center space-x-2 border-b border-gray-800 pb-4">
        <Bot className="h-8 w-8 text-blue-400" />
        <div className="flex flex-col">
          <span className="text-lg font-bold text-white">ATW MONADO</span>
          <span className="text-xs font-mono text-blue-400">Vision Engine v3.0</span>
        </div>
      </div>
      
      <div className="mt-6 flex-1 space-y-4">
        <SidebarSection title="Home">
          <SidebarButton
            id="dashboard"
            icon={LayoutDashboard}
            text="Nexus Dashboard"
            isActive={activeView === 'dashboard'}
            onClick={() => setActiveView('dashboard')}
          />
        </SidebarSection>
        
        <SidebarSection title="Intelligence Layers">
          <SidebarButton id="l1" icon={Compass} text="Market Vision AI" isActive={activeView === 'l1'} onClick={() => setActiveView('l1')} />
          <SidebarButton id="l2" icon={Shield} text="Competitive Foresight" isActive={activeView === 'l2'} onClick={() => setActiveView('l2')} />
          <SidebarButton id="l3" icon={BrainCircuit} text="Innovation Pulse" isActive={activeView === 'l3'} onClick={() => setActiveView('l3')} />
          <SidebarButton id="l4" icon={Target} text="Client Intelligence" isActive={activeView === 'l4'} onClick={() => setActiveView('l4')} />
        </SidebarSection>
        
        <SidebarSection title="Action">
          <SidebarButton id="pipeline" icon={ListTodo} text="Opportunity Pipeline" isActive={activeView === 'pipeline'} onClick={() => setActiveView('pipeline')} />
          <SidebarButton id="reports" icon={FileText} text="Strategic Reports" isActive={activeView === 'reports'} onClick={() => setActiveView('reports')} />
        </SidebarSection>
      </div>
      
      <div className="border-t border-gray-800 pt-4">
        <span className="text-center text-xs text-gray-500">
          © Aryan Tech World Pvt. Ltd.
        </span>
      </div>
    </nav>
  );
}

function SidebarSection({ title, children }) {
  return (
    <div>
      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-gray-500">{title}</h3>
      <div className="space-y-1">
        {children}
      </div>
    </div>
  );
}

function SidebarButton({ id, icon: Icon, text, isActive, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`group flex w-full items-center space-x-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all ${
        isActive
          ? 'bg-blue-600 text-white shadow-lg'
          : 'text-gray-400 hover:bg-gray-800 hover:text-white'
      }`}
    >
      <Icon className={`h-5 w-5 ${isActive ? 'text-white' : 'text-gray-500 group-hover:text-white'}`} />
      <span>{text}</span>
    </button>
  );
}

// --- Header Component ---
function Header({ title, subtitle, children }) {
  return (
    <div className="mb-6 flex flex-col items-start justify-between gap-4 md:flex-row md:items-center">
      <div>
        <h1 className="text-3xl font-bold text-white">{title}</h1>
        <p className="mt-1 text-base text-gray-400">{subtitle}</p>
      </div>
      <div className="flex flex-shrink-0 space-x-2">
        {children}
      </div>
    </div>
  );
}

// --- Card Component ---
function Card({ className, children }) {
  return (
    <div className={`rounded-2xl border border-gray-800 bg-gray-900/50 p-6 shadow-xl ${className}`}>
      {children}
    </div>
  );
}

// --- Button Component ---
function Button({ icon: Icon, text, onClick, isLoading = false, variant = 'primary' }) {
  const baseClasses = "flex items-center justify-center space-x-2 rounded-lg px-4 py-2 text-sm font-semibold transition-all shadow-md";
  const variants = {
    primary: "bg-blue-600 text-white hover:bg-blue-500 disabled:bg-blue-800",
    secondary: "bg-gray-700 text-gray-200 hover:bg-gray-600 disabled:bg-gray-800",
  };
  
  return (
    <button
      onClick={onClick}
      disabled={isLoading}
      className={`${baseClasses} ${variants[variant]}`}
    >
      {isLoading ? (
        <Loader2 className="h-5 w-5 animate-spin" />
      ) : (
        Icon && <Icon className="h-5 w-5" />
      )}
      <span>{isLoading ? 'Loading...' : text}</span>
    </button>
  );
}

// --- Error/Loading Component ---
function ApiStall({ error, isLoading, layerName, onGenerate }) {
  if (error) {
    return (
      <Card className="border-red-600/50 bg-red-900/20">
        <div className="flex flex-col items-center text-center">
          <ServerCrash className="h-12 w-12 text-red-400" />
          <h3 className="mt-2 text-xl font-semibold text-white">Connection Error</h3>
          <pre className="mt-2 w-full overflow-auto rounded-lg bg-gray-950 p-4 text-left text-xs text-red-300">
            {error}
          </pre>
          <Button
            icon={Zap}
            text={`Retry ${layerName}`}
            onClick={onGenerate}
            isLoading={isLoading}
            variant="secondary"
            className="mt-4"
          />
        </div>
      </Card>
    );
  }
  
  if (isLoading) {
    return (
      <Card>
        <div className="flex flex-col items-center justify-center py-12">
          <Loader2 className="h-12 w-12 animate-spin text-blue-400" />
          <p className="mt-4 text-lg font-medium text-gray-300">Fetching {layerName}...</p>
          <p className="text-sm text-gray-500">Monado AI is analyzing real-time data.</p>
        </div>
      </Card>
    );
  }
  
  return null;
}


// --- L5: Strategic Nexus Dashboard ---
function StrategicNexusDashboard({ insights, alerts, pipelineSummary }) {
  const pipelineCounts = pipelineSummary.cards.reduce((acc, card) => {
    acc[card.column] = (acc[card.column] || 0) + 1;
    return acc;
  }, {});

  return (
    <div>
      <Header
        title="Strategic Nexus Dashboard"
        subtitle="Monado's unified decision layer. All intelligence streams converge here."
      />
      
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* --- Main Column --- */}
        <div className="space-y-6 lg:col-span-2">
          <Card>
            <h2 className="mb-4 text-xl font-semibold text-white">Real-Time Insight Feed</h2>
            <div className="space-y-4">
              {insights.length > 0 ? insights.map(insight => (
                <InsightCard key={insight.id} {...insight} />
              )) : (
                 <div className="text-center text-gray-500 py-8">
                   <p>No new insights. Refresh intelligence layers to populate feed.</p>
                 </div>
              )}
            </div>
          </Card>
        </div>

        {/* --- Right Sidebar --- */}
        <div className="space-y-6 lg:col-span-1">
          <Card>
            <h2 className="mb-4 text-xl font-semibold text-white">Foresight Alerts</h2>
            <ForesightAlerts alerts={alerts} />
          </Card>
          
          <Card>
            <h2 className="mb-4 text-xl font-semibold text-white">Opportunity Pipeline</h2>
            <div className="space-y-3">
              {pipelineSummary.columns.map(col => (
                <div key={col.id} className="flex items-center justify-between rounded-lg bg-gray-800 p-3">
                  <span className="text-sm font-medium text-gray-300">{col.title}</span>
                  <span className="rounded-full bg-blue-600 px-2.5 py-0.5 text-xs font-bold text-white">
                    {pipelineCounts[col.id] || 0}
                  </span>
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}

function InsightCard({ type, title, text, time }) {
  const Icon = iconMap[type] || iconMap.default;
  const colors = {
    market: "border-green-600/50",
    competitor: "border-red-600/50",
    tech: "border-blue-600/50",
    client: "border-yellow-600/50",
    default: "border-gray-600/50",
  };
  
  return (
    <div className={`flex items-start space-x-4 rounded-lg border-l-4 bg-gray-800/60 p-4 ${colors[type] || colors.default}`}>
      <div className="flex-shrink-0">{Icon}</div>
      <div className="flex-1">
        <h3 className="text-base font-semibold text-white">{title}</h3>
        <p className="text-sm text-gray-400">{text}</p>
      </div>
      <span className="text-xs text-gray-500">{time}</span>
    </div>
  );
}

function ForesightAlerts({ alerts }) {
  const getIcon = (type) => {
    if (type === 'competitor') return <Shield className="h-5 w-5 text-red-400" />;
    if (type === 'client') return <Target className="h-5 w-5 text-yellow-400" />;
    if (type === 'tech') return <Zap className="h-5 w-5 text-blue-400" />;
    return <Bell className="h-5 w-5 text-gray-400" />;
  };

  return (
    <ul className="space-y-3">
      {alerts.map(alert => (
        <li key={alert.id} className="flex items-start space-x-3">
          <div className="flex-shrink-0 pt-0.5">{getIcon(alert.type)}</div>
          <span className="text-sm text-gray-300">{alert.text}</span>
        </li>
      ))}
    </ul>
  );
}

// --- L1: Market Vision AI ---
function MarketVisionAI({ data, onGenerate, isLoading, error }) {
  if (error || isLoading) {
    return (
      <div>
        <Header 
          title="L1: Market Vision AI" 
          subtitle="Forecast SaaS/AI trends & client industry opportunities."
        />
        <ApiStall error={error} isLoading={isLoading} layerName="L1 Vision Report" onGenerate={onGenerate} />
      </div>
    );
  }

  return (
    <div>
      <Header 
        title="L1: Market Vision AI" 
        subtitle="Forecast SaaS/AI trends & client industry opportunities."
      >
        <Button 
          icon={Zap} 
          text="Generate New Vision Report" 
          onClick={onGenerate} 
          isLoading={isLoading} 
        />
      </Header>
      
      <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
        <div className="xl:col-span-2">
          <Card>
            <h2 className="mb-4 text-xl font-semibold text-white">AI-Generated Forecast</h2>
            <div className="prose prose-sm prose-invert max-w-none prose-p:text-gray-300 prose-strong:text-white">
              <MarkdownRenderer content={data.forecast} />
            </div>
          </Card>
        </div>
        
        <div className="space-y-6 xl:col-span-1">
          <Card>
            <h2 className="mb-4 text-xl font-semibold text-white">Sector Heatmap</h2>
            <ScoreList items={data.sectors} />
          </Card>
          <Card>
            <h2 className="mb-4 text-xl font-semibold text-white">Opportunity Scoring</h2>
            <ScoreList items={data.opportunities} />
          </Card>
        </div>
      </div>
    </div>
  );
}

function ScoreList({ items }) {
  if (!items || items.length === 0) {
    return <p className="text-sm text-gray-500">No data available. Click generate to fetch.</p>;
  }
  return (
    <ul className="space-y-3">
      {items.map((item) => (
        <li key={item.name} className="flex items-center justify-between space-x-3">
          <span className="text-sm text-gray-300">{item.name}</span>
          <span className="flex-shrink-0 rounded-full bg-blue-600 px-2.5 py-0.5 text-xs font-bold text-white">
            {item.score}
          </span>
        </li>
      ))}
    </ul>
  );
}

// --- L2: Competitive Foresight Hub ---
function CompetitiveForesightHub({ data, onGenerate, isLoading, error }) {
  if (error || isLoading) {
    return (
      <div>
        <Header 
          title="L2: Competitive Foresight Hub" 
          subtitle="Continuously monitor and rank competitors’ activities."
        />
        <ApiStall error={error} isLoading={isLoading} layerName="L2 Intelligence" onGenerate={onGenerate} />
      </div>
    );
  }

  return (
    <div>
      <Header
        title="L2: Competitive Foresight Hub"
        subtitle="Continuously monitor and rank competitors’ activities."
      >
        <Button 
          icon={Zap} 
          text="Refresh Intelligence" 
          onClick={onGenerate} 
          isLoading={isLoading} 
        />
      </Header>
      
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card>
          <h2 className="mb-4 text-xl font-semibold text-white">Real-time Activity Feed</h2>
          <ul className="space-y-4">
            {data.activity.length > 0 ? data.activity.map(item => (
              <li key={item.id} className="flex items-center space-x-3">
                <span className="rounded-lg bg-gray-700 px-3 py-1 text-sm font-medium text-gray-200">
                  {item.name}
                </span>
                <p className="flex-1 text-sm text-gray-300">{item.activity}</p>
                <span className="text-xs text-gray-500">{item.time}</span>
              </li>
            )) : <p className="text-sm text-gray-500">No activity data. Click refresh to fetch.</p>}
          </ul>
        </Card>
        
        <Card>
          <h2 className="mb-4 text-xl font-semibold text-white">Auto-Generated SWOT Matrix</h2>
          <SwotMatrixDisplay swot={data.swot} />
        </Card>
      </div>
    </div>
  );
}

function SwotMatrixDisplay({ swot }) {
  return (
    <div className="grid grid-cols-2 gap-4">
      <SwotCategory title="Strengths" icon={ThumbsUp} items={swot.strengths} className="text-green-400" />
      <SwotCategory title="Weaknesses" icon={ThumbsDown} items={swot.weaknesses} className="text-red-400" />
      <SwotCategory title="Opportunities" icon={Sparkles} items={swot.opportunities} className="text-blue-400" />
      <SwotCategory title="Threats" icon={AlertTriangle} items={swot.threats} className="text-yellow-400" />
    </div>
  );
}

function SwotCategory({ title, icon: Icon, items, className }) {
  return (
    <div className="rounded-lg bg-gray-800/70 p-4">
      <h3 className={`mb-2 flex items-center text-base font-semibold ${className}`}>
        <Icon className="mr-2 h-5 w-5" />
        {title}
      </h3>
      <ul className="list-inside list-disc space-y-1 text-sm text-gray-300">
        {items.length > 0 ? items.map((item, index) => <li key={index}>{item}</li>) : <li>N/A</li>}
      </ul>
    </div>
  );
}

// --- L3: Innovation Pulse Tracker ---
function InnovationPulseTracker({ data, onGenerate, isLoading, error }) {
  if (error || isLoading) {
    return (
      <div>
        <Header 
          title="L3: Innovation Pulse Tracker" 
          subtitle="Detect rising technologies, APIs, and developer ecosystems."
        />
        <ApiStall error={error} isLoading={isLoading} layerName="L3 Pulse" onGenerate={onGenerate} />
      </div>
    );
  }

  return (
    <div>
      <Header
        title="L3: Innovation Pulse Tracker"
        subtitle="Detect rising technologies, APIs, and developer ecosystems."
      >
        <Button 
          icon={Zap} 
          text="Refresh Pulse" 
          onClick={onGenerate} 
          isLoading={isLoading} 
        />
      </Header>
      
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <Card>
            <h2 className="mb-4 text-xl font-semibold text-white">Innovation Map</h2>
            <div className="flex gap-6">
              <div className="flex-1">
                <h3 className="mb-3 text-base font-semibold text-green-400">What's Hot</h3>
                <ul className="list-inside list-disc space-y-1 text-sm text-gray-300">
                  {data.map.hot.length > 0 ? data.map.hot.map(item => <li key={item}>{item}</li>) : <li>N/A</li>}
                </ul>
              </div>
              <div className="flex-1">
                <h3 className="mb-3 text-base font-semibold text-red-400">What's Fading</h3>
                <ul className="list-inside list-disc space-y-1 text-sm text-gray-300">
                  {data.map.fading.length > 0 ? data.map.fading.map(item => <li key={item}>{item}</li>) : <li>N/A</li>}
                </ul>
              </div>
            </div>
          </Card>
          <Card>
            <h2 className="mb-4 text-xl font-semibold text-white">R&D Idea Bank</h2>
            <ul className="space-y-3">
              {data.bank.length > 0 ? data.bank.map(item => (
                <li key={item.id} className="flex items-center justify-between rounded-lg bg-gray-800 p-3">
                  <div className="flex items-center space-x-3">
                    <FlaskConical className="h-5 w-5 text-blue-400" />
                    <span className="text-sm text-gray-300">{item.text}</span>
                  </div>
                  <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                    item.priority === 'High' ? 'bg-red-600 text-white' :
                    item.priority === 'Medium' ? 'bg-yellow-600 text-black' : 'bg-gray-600 text-white'
                  }`}>
                    {item.priority}
                  </span>
                </li>
              )) : <p className="text-sm text-gray-500">No R&D ideas. Click refresh to fetch.</p>}
            </ul>
          </Card>
        </div>
        
        <div className="lg:col-span-1">
          <Card>
            <h2 className="mb-4 text-xl font-semibold text-white">Developer Momentum</h2>
            <ul className="space-y-3">
              {data.momentum.length > 0 ? data.momentum.map(item => (
                <li key={item.id} className="rounded-lg bg-gray-800 p-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <Github className="h-4 w-4 text-gray-400" />
                      <span className="text-sm font-medium text-white">{item.name}</span>
                    </div>
                    <span className={`flex items-center text-xs font-bold ${item.change >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                      {item.change >= 0 ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />}
                      {item.change}
                    </span>
                  </div>
                  <div className="mt-1 text-right text-xs text-gray-400">{item.stars.toLocaleString()} stars</div>
                </li>
              )) : <p className="text-sm text-gray-500">No repo data. Click refresh to fetch.</p>}
            </ul>
          </Card>
        </div>
      </div>
    </div>
  );
}

// --- L4: Client Intelligence Engine ---
function ClientIntelligenceEngine({ data, onGenerate, isLoading, error }) {
  if (error || isLoading) {
    return (
      <div>
        <Header 
          title="L4: Client Intelligence Engine" 
          subtitle="Profile, score, and predict potential clients and sectors."
        />
        <ApiStall error={error} isLoading={isLoading} layerName="L4 Client List" onGenerate={onGenerate} />
      </div>
    );
  }

  return (
    <div>
      <Header
        title="L4: Client Intelligence Engine"
        subtitle="Profile, score, and predict potential clients and sectors."
      >
        <Button 
          icon={Zap} 
          text="Refresh Client List" 
          onClick={onGenerate} 
          isLoading={isLoading} 
        />
      </Header>
      
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <Card>
            <h2 className="mb-4 text-xl font-semibold text-white">Top 50 Potential Clients</h2>
            <ClientListTable clients={data.clients} />
          </Card>
        </div>
        
        <div className="lg:col-span-1">
          <Card>
            <h2 className="mb-4 text-xl font-semibold text-white">Lead Heatmap (by Sector)</h2>
            <LeadHeatmap items={data.heatmap} />
          </Card>
        </div>
      </div>
    </div>
  );
}

function ClientListTable({ clients }) {
  if (!clients || clients.length === 0) {
    return <p className="text-sm text-gray-500">No client data. Click refresh to fetch.</p>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-800">
        <thead className="bg-gray-800/50">
          <tr>
            <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-400">Company</th>
            <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-400">Sector</th>
            <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-400">Readiness Score</th>
            <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-400">Conversion Prob.</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-800">
          {clients.map(client => (
            <tr key={client.id} className="hover:bg-gray-800/40">
              <td className="px-4 py-3 text-sm font-medium text-white">{client.name}</td>
              <td className="px-4 py-3 text-sm text-gray-400">{client.sector}</td>
              <td className="px-4 py-3 text-sm font-semibold text-blue-400">{client.readinessScore}</td>
              <td className="px-4 py-3 text-sm font-semibold text-green-400">{client.conversionProb}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function LeadHeatmap({ items }) {
  if (!items || items.length === 0) {
    return <p className="text-sm text-gray-500">No heatmap data. Click refresh to fetch.</p>;
  }
  
  const maxPotential = Math.max(100, ...items.map(i => i.potential));

  return (
    <ul className="space-y-2">
      {items.map(item => (
        <li key={item.sector}>
          <div className="mb-1 flex justify-between text-xs font-medium text-gray-300">
            <span>{item.sector} ({item.size} leads)</span>
            <span>{item.potential}%</span>
          </div>
          <div className="h-2.5 w-full rounded-full bg-gray-700">
            <div 
              className="h-2.5 rounded-full bg-gradient-to-r from-blue-500 to-purple-500" 
              style={{ width: `${(item.potential / maxPotential) * 100}%` }}
            ></div>
          </div>
        </li>
      ))}
    </ul>
  );
}

// --- L5: Opportunity Pipeline (Kanban) ---
function OpportunityPipeline({ data, onCardDrop }) {
  
  const handleDragStart = (e, cardId) => {
    e.dataTransfer.setData("cardId", cardId);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e, columnId) => {
    e.preventDefault();
    const cardId = e.dataTransfer.getData("cardId");
    onCardDrop(cardId, columnId);
  };
  
  return (
    <div>
      <Header
        title="Opportunity Pipeline Board"
        subtitle="Drag-and-drop insights to track them from detection to strategic action."
      />
      
      <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-4">
        {data.columns.map(col => (
          <div 
            key={col.id} 
            className="rounded-xl bg-gray-950/70 p-4"
            onDragOver={handleDragOver}
            onDrop={(e) => handleDrop(e, col.id)}
          >
            <h2 className="mb-4 text-base font-semibold uppercase tracking-wider text-gray-400">{col.title}</h2>
            <div className="space-y-4">
              {data.cards
                .filter(card => card.column === col.id)
                .map(card => (
                  <PipelineCard 
                    key={card.id} 
                    {...card} 
                    onDragStart={(e) => handleDragStart(e, card.id)} 
                  />
                ))
              }
              {data.cards.filter(card => card.column === col.id).length === 0 && (
                <div className="rounded-lg border-2 border-dashed border-gray-700 p-4 text-center text-sm text-gray-600">
                  Drop cards here
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function PipelineCard({ id, title, content, type, onDragStart }) {
  const Icon = iconMap[type] || iconMap.default;
  const colors = {
    market: "border-green-600/70",
    competitor: "border-red-600/70",
    tech: "border-blue-600/70",
    client: "border-yellow-600/70",
    default: "border-gray-600/70",
  };

  return (
    <div
      draggable
      onDragStart={onDragStart}
      className={`cursor-grab rounded-lg border-l-4 bg-gray-800 p-4 shadow-lg active:cursor-grabbing ${colors[type] || colors.default}`}
    >
      <div className="flex items-start justify-between">
        <h3 className="text-base font-semibold text-white">{title}</h3>
        <span title={type}>{Icon}</span>
      </div>
      <p className="mt-1 text-sm text-gray-400">{content}</p>
    </div>
  );
}

// --- Action: Strategic Reports ---
function StrategicReports({ layers, report, setReport, isLoading }) {

  const generateReportText = () => {
    if (!layers.l1.forecast || !layers.l2.swot.threats.length || !layers.l3.map.hot.length || !layers.l4.clients.length) {
      return null;
    }
    
    const l1_summary = layers.l1.opportunities[0]?.name || "N/A";
    const l2_summary = layers.l2.swot.threats[0] || "N/A";
    const l3_summary = layers.l3.map.hot[0] || "N/A";
    const l4_summary = `${layers.l4.clients[0]?.name} (Score: ${layers.l4.clients[0]?.readinessScore})` || "N/A";
    
    const recommendations = [
      `ACTION: Prioritize R&D on '${l3_summary}' to counter threat '${l2_summary}'.`,
      `REVIEW: Fast-track pitch for '${l4_summary}' based on L1 trend '${l1_summary}'.`,
      `MONITOR: Keep close watch on '${layers.l2.activity[0]?.name}'s' next move.`
    ];

    return {
      title: `ATW Strategic Vision Report - ${new Date().toLocaleDateString()}`,
      l1_summary, l2_summary, l3_summary, l4_summary,
      recommendations
    };
  };

  const handleGenerate = () => {
    const generatedReport = generateReportText();
    setReport(generatedReport);
  };
  
  const handleDownload = () => {
    if (!report) return;
    
    const content = `
ATW MONADO FRAMEWORK™
STRATEGIC VISION REPORT
=========================
Generated: ${new Date().toLocaleString()}
Title: ${report.title}

---
1. EXECUTIVE SUMMARY
---
This report synthesizes real-time data from all four (L1-L4) Monado intelligence layers to provide a single, actionable strategic directive.

---
2. KEY INTELLIGENCE SIGNALS
---
* L1 Market Vision: ${report.l1_summary}
* L2 Competitive Foresight: ${report.l2_summary}
* L3 Innovation Pulse: ${report.l3_summary}
* L4 Client Intelligence: ${report.l4_summary}

---
3. STRATEGIC RECOMMENDATIONS (ACTION-REVIEW-MONITOR)
---
${report.recommendations.map((rec, i) => `${i+1}. ${rec}`).join('\n')}

---
END OF REPORT
    `;
    
    const blob = new Blob([content.trim()], { type: 'text/plain' });
    const href = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = href;
    link.download = `ATW_Monado_Report_${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(href);
  };

  return (
    <div>
      <Header
        title="Strategic Vision Reports"
        subtitle="Generate executive-ready PDF summaries of all intelligence streams."
      >
        <Button 
          icon={Zap} 
          text="Generate New Report" 
          onClick={handleGenerate} 
          isLoading={isLoading}
          disabled={!layers.l1.forecast || !layers.l2.swot.threats.length}
        />
      </Header>
      
      {!report && (
        <Card className="text-center">
          <FileText className="mx-auto h-12 w-12 text-gray-600" />
          <h3 className="mt-2 text-xl font-semibold text-white">No Report Generated</h3>
          <p className="mt-1 text-sm text-gray-400">
            Click "Generate New Report" to synthesize data from all live intelligence layers.
          </p>
          {(!layers.l1.forecast || !layers.l2.swot.threats.length) && (
             <p className="mt-4 text-xs text-yellow-500 bg-yellow-900/30 rounded-lg p-2">
               Warning: Please run L1, L2, L3, and L4 reports first to ensure all data is available.
             </p>
          )}
        </Card>
      )}
      
      {report && (
        <div className="print:bg-white print:text-black">
          <div className="mb-4 flex justify-end space-x-2 print:hidden">
            <Button 
              icon={Download} 
              text="Download (.txt)" 
              onClick={handleDownload} 
              variant="secondary"
            />
            <Button 
              icon={Printer} 
              text="Print" 
              onClick={() => window.print()} 
              variant="secondary"
            />
          </div>
          
          <Card className="mx-auto max-w-4xl p-8 md:p-12 print:border-none print:p-0 print:shadow-none">
            <h1 className="text-center text-3xl font-bold text-white print:text-black">{report.title}</h1>
            <p className="text-center text-sm text-gray-400 print:text-gray-700">Generated by ATW MONADO FRAMEWORK™ (Vision Engine v3.0)</p>
            
            <ReportSection title="1. Executive Summary">
              <p className="text-gray-300 print:text-black">
                This report synthesizes real-time data from all four (L1-L4) Monado intelligence layers
                to provide a single, actionable strategic directive.
              </p>
            </ReportSection>
            
            <ReportSection title="2. Key Intelligence Signals">
              <ul className="list-inside list-disc space-y-2 text-gray-300 print:text-black">
                <li className="print:text-black"><strong className="text-white print:text-black">L1 Market Vision:</strong> {report.l1_summary}</li>
                <li className="print:text-black"><strong className="text-white print:text-black">L2 Competitive Foresight:</strong> {report.l2_summary}</li>
                <li className="print:text-black"><strong className="text-white print:text-black">L3 Innovation Pulse:</strong> {report.l3_summary}</li>
                <li className="print:text-black"><strong className="text-white print:text-black">L4 Client:</strong> {report.l4_summary}</li>
              </ul>
            </ReportSection>
            
            <ReportSection title="3. Strategic Recommendations (Action-Review-Monitor)">
              <ul className="list-inside list-disc space-y-2 text-gray-300 print:text-black">
                {report.recommendations.map((rec, index) => (
                  <li key={index} className="text-white font-medium print:text-black">{rec}</li>
                ))}
              </ul>
            </ReportSection>
            
            <div className="border-t border-gray-700 mt-8 pt-4 print:border-gray-300">
              <p className="text-xs text-gray-500 text-center print:text-gray-700">
                Generated by ATW MONADO FRAMEWORK™ (Vision Engine v3.0)
              </p>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}

function ReportSection({ title, children }) {
  return (
    <div className="mt-8 border-t border-gray-700 pt-6 print:border-gray-300">
      <h2 className="text-xl font-semibold text-white print:text-black">{title}</h2>
      <div className="mt-3">{children}</div>
    </div>
  );
}

// --- Markdown Renderer ---
// A simple renderer to handle bolding and newlines
function MarkdownRenderer({ content }) {
  if (!content) return null;

  const lines = content.split('\n');

  return (
    <>
      {lines.map((line, index) => {
        if (line.trim() === '') {
          return <br key={index} />;
        }
        
        const parts = line.split(/(\*\*.*?\*\*)/g); // Split by bold
        
        return (
          <p key={index}>
            {parts.map((part, i) => {
              if (part.startsWith('**') && part.endsWith('**')) {
                return <strong key={i}>{part.substring(2, part.length - 2)}</strong>;
              }
              return <span key={i}>{part}</span>;
            })}
          </p>
        );
      })}
    </>
  );
}



